import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  ArrowLeft, Loader2, Shield, ShoppingBag, Mail, Send, Edit2, Save, X,
  UserCircle, MapPin, Clock, DollarSign, AlertTriangle, CheckCircle,
  FileText, Zap, Eye, ShieldOff, PlusCircle, Link2, Unlink,
} from 'lucide-react';
import { toast } from 'sonner';
import { errText } from '@/lib/errText';

const STATUS_LABELS = {
  order_received: 'Order Received', in_review: 'In Review',
  more_images_requested: 'More Images', images_received: 'Images Received',
  authentic: 'Authentic', counterfeit: 'Counterfeit',
  appraisal_complete: 'Appraisal Done', unable_to_determine: 'Unable',
  refunded: 'Refunded', revoked: 'Revoked', paid: 'Paid',
};

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [profileForm, setProfileForm] = useState({});
  const [editingProfile, setEditingProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [orderCat, setOrderCat] = useState('');
  const [orderBrand, setOrderBrand] = useState('');
  const [orderTitle, setOrderTitle] = useState('');
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupEmail, setGroupEmail] = useState('');

  useEffect(() => {
    api.get('/catalog/categories').then(r => setCategories(r.data)).catch(() => {});
    api.get('/catalog/brands').then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const r = await api.get(`/admin/users/${userId}`);
      setUser(r.data);
      setEditForm({ display_name: r.data.display_name || '' });
      setProfileForm({
        first_name: r.data.profile?.first_name || '',
        last_name: r.data.profile?.last_name || '',
        phone: r.data.profile?.phone || '',
        notes: r.data.profile?.notes || '',
      });
      setNewEmail(r.data.email || '');
    } catch { toast.error('Failed to load user'); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { loadUser(); }, [loadUser]);

  const saveDisplayName = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${userId}`, { display_name: editForm.display_name });
      toast.success('Name updated');
      setEditing(false);
      await loadUser();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${userId}/profile`, profileForm);
      toast.success('Profile updated');
      setEditingProfile(false);
      await loadUser();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const changeEmail = async () => {
    if (!newEmail.trim()) return;
    setSaving(true);
    try {
      await api.put(`/admin/users/${userId}`, { email: newEmail.trim() });
      toast.success('Email updated');
      setEmailDialogOpen(false);
      await loadUser();
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const toggleAdmin = async () => {
    const isAdmin = user.roles?.includes('admin');
    try {
      await api.put(`/admin/users/${userId}/role`, {
        role: 'admin',
        action: isAdmin ? 'revoke' : 'grant'
      });
      toast.success(isAdmin ? 'Admin role revoked' : 'Admin role granted');
      await loadUser();
    } catch (e) { toast.error(errText(e)); }
  };

  const setStaffRole = async (nextRole) => {
    // AUTH-06 UI: grants admin (idempotent) and sets staff_role in one call.
    // Backend accepts staff_role only when action=grant + role=admin.
    if (!nextRole) return;
    try {
      await api.put(`/admin/users/${userId}/role`, {
        role: 'admin',
        action: 'grant',
        staff_role: nextRole,
      });
      toast.success(nextRole === 'full_admin' ? 'Full Admin assigned' : 'Authenticator assigned');
      await loadUser();
    } catch (e) { toast.error(errText(e, 'Failed to update staff privilege')); }
  };

  const toggleActive = async () => {
    try {
      await api.put(`/admin/users/${userId}`, { is_active: !user.is_active });
      toast.success(user.is_active ? 'Account deactivated' : 'Account activated');
      await loadUser();
    } catch (e) { toast.error(errText(e)); }
  };

  const sendMagicLink = async () => {
    try {
      const r = await api.post(`/admin/users/${userId}/send-magic-link`);
      toast.success(r.data.message);
    } catch (e) { toast.error(errText(e)); }
  };

  const createManualOrder = async () => {
    if (!orderCat) { toast.error('Pick a category'); return; }
    setSaving(true);
    try {
      const r = await api.post(`/team/users/${userId}/manual-order`, {
        category_slug: orderCat,
        item_title: orderTitle,
        brand_slug: orderBrand || undefined,
      });
      toast.success(`Order ${r.data.order_number} created`);
      setOrderDialogOpen(false);
      navigate(`/team/items/${r.data.item_id}`);
    } catch (e) { toast.error(errText(e, 'Failed to create order')); }
    finally { setSaving(false); }
  };

  const groupAccounts = async () => {
    if (!groupEmail.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/users/${userId}/group`, { email: groupEmail.trim() });
      toast.success('Accounts grouped');
      setGroupDialogOpen(false);
      setGroupEmail('');
      await loadUser();
    } catch (e) { toast.error(errText(e, 'Failed to group')); }
    finally { setSaving(false); }
  };

  const ungroupAccount = async () => {
    if (!confirm('Remove this account from its group?')) return;
    try {
      await api.post(`/admin/users/${userId}/ungroup`);
      toast.success('Account ungrouped');
      await loadUser();
    } catch (e) { toast.error(errText(e)); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!user) return <div className="text-center py-12"><p className="text-sm text-muted-foreground">User not found.</p></div>;

  const isAdmin = user.roles?.includes('admin');

  return (
    <div data-testid="user-detail-page">
      <Link to="/team/users" className="inline-flex items-center gap-1 text-xs text-muted-foreground no-underline hover:text-ink mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> All Users
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{
            background: isAdmin ? 'rgba(14,107,93,0.10)' : 'hsl(var(--bg-paper-2))'
          }}>
            <span className="text-lg font-semibold" style={{ color: isAdmin ? '#0E6B5D' : 'hsl(var(--muted-ink))' }}>
              {(user.display_name || user.email || '?')[0].toUpperCase()}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              {editing ? (
                <div className="flex items-center gap-1">
                  <Input value={editForm.display_name} onChange={e => setEditForm({...editForm, display_name: e.target.value})}
                    className="h-7 text-sm w-48" placeholder="Display name" autoFocus />
                  <Button size="sm" className="h-7 text-xs" onClick={saveDisplayName} disabled={saving}><Save className="h-3 w-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(false)}><X className="h-3 w-3" /></Button>
                </div>
              ) : (
                <>
                  <h1 className="text-lg font-semibold text-ink" data-testid="user-detail-name">{user.display_name || user.email.split('@')[0]}</h1>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground" onClick={() => setEditing(true)}><Edit2 className="h-3 w-3" /></Button>
                </>
              )}
              {isAdmin && (
                <Badge className="text-[9px]" style={{ background: 'rgba(14,107,93,0.10)', color: '#0E6B5D' }}><Shield className="h-2.5 w-2.5 mr-0.5" /> Admin</Badge>
              )}
              {user.is_active === false && (
                <Badge variant="outline" className="text-[9px] text-red-600 border-red-200">Deactivated</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{user.email}</p>
            <p className="text-[10px] text-muted-foreground">Joined {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Left column: Profile + Orders */}
        <div className="space-y-4">
          {/* Profile info */}
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><UserCircle className="h-3 w-3" /> Profile</h3>
                {editingProfile ? (
                  <div className="flex gap-1">
                    <Button size="sm" className="h-6 text-[10px]" onClick={saveProfile} disabled={saving}><Save className="h-3 w-3 mr-0.5" /> Save</Button>
                    <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setEditingProfile(false)}>Cancel</Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="h-6 text-[10px] gap-0.5" onClick={() => setEditingProfile(true)}><Edit2 className="h-3 w-3" /> Edit</Button>
                )}
              </div>
              {editingProfile ? (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">First Name</Label><Input value={profileForm.first_name} onChange={e => setProfileForm({...profileForm, first_name: e.target.value})} className="mt-1 h-8 text-xs" /></div>
                  <div><Label className="text-xs">Last Name</Label><Input value={profileForm.last_name} onChange={e => setProfileForm({...profileForm, last_name: e.target.value})} className="mt-1 h-8 text-xs" /></div>
                  <div><Label className="text-xs">Phone</Label><Input value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} className="mt-1 h-8 text-xs" /></div>
                  <div className="col-span-2"><Label className="text-xs">Notes</Label><textarea value={profileForm.notes} onChange={e => setProfileForm({...profileForm, notes: e.target.value})} className="w-full mt-1 p-2 rounded border border-stone text-xs min-h-[60px] resize-y focus:outline-none focus:ring-1 focus:ring-ring/30" /></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground text-xs">First Name</span><p className="font-medium text-xs">{user.profile?.first_name || '-'}</p></div>
                  <div><span className="text-muted-foreground text-xs">Last Name</span><p className="font-medium text-xs">{user.profile?.last_name || '-'}</p></div>
                  <div><span className="text-muted-foreground text-xs">Phone</span><p className="font-medium text-xs">{user.profile?.phone || '-'}</p></div>
                  <div><span className="text-muted-foreground text-xs">Notes</span><p className="font-medium text-xs">{user.profile?.notes || '-'}</p></div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order History */}
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                  <ShoppingBag className="h-3 w-3" /> Order History ({user.order_count})
                </h3>
                <span className="text-xs font-semibold text-ink">${(user.total_spent_cents / 100).toFixed(2)} total</span>
              </div>
              {(user.orders || []).length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No orders yet.</p>
              ) : (
                <div className="space-y-2">
                  {user.orders.map(order => (
                    <Link key={order.id} to={`/team/items/${order.items?.[0]?.id || ''}`} className="block no-underline">
                      <div className="flex items-center justify-between p-2 rounded-lg hover:bg-stone/5 transition-colors">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-ink">{order.order_number}</span>
                            <Badge variant="outline" className="text-[9px]">{STATUS_LABELS[order.status] || order.status}</Badge>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {order.items_count} item{order.items_count !== 1 ? 's' : ''}
                            {order.items?.[0]?.item_title ? ` — ${order.items[0].item_title}` : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-medium text-ink">${(order.total_cents / 100).toFixed(2)}</span>
                          <p className="text-[10px] text-muted-foreground">{order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Addresses */}
          {user.addresses && user.addresses.length > 0 && (
            <Card className="bg-white border-stone/50">
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-3"><MapPin className="h-3 w-3" /> Addresses</h3>
                {user.addresses.map((a, i) => (
                  <div key={i} className="text-xs text-ink p-2 rounded bg-stone/5 mb-1">
                    {a.line_1}{a.line_2 ? `, ${a.line_2}` : ''}, {a.city}, {a.state} {a.postal_code} {a.country}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column: Actions */}
        <div className="space-y-4">
          {/* Quick Actions */}
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Actions</h3>
              <div className="space-y-2">
                <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 justify-start" data-testid="new-manual-order-btn">
                      <PlusCircle className="h-3.5 w-3.5" /> New Order (No Charge)
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle className="text-sm">Create Manual Order</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground">Creates a $0 order with one blank item for <strong>{user.email}</strong>. You can edit details, upload photos and set a determination on the item page. The customer receives the usual order confirmation email.</p>
                      <div><Label className="text-xs">Category</Label>
                        <select value={orderCat} onChange={e => setOrderCat(e.target.value)} className="w-full mt-1 p-2 rounded border border-stone text-xs" data-testid="manual-order-category">
                          <option value="">Select category...</option>
                          {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                        </select>
                      </div>
                      <div><Label className="text-xs">Brand (optional)</Label>
                        <select value={orderBrand} onChange={e => setOrderBrand(e.target.value)} className="w-full mt-1 p-2 rounded border border-stone text-xs" data-testid="manual-order-brand">
                          <option value="">No brand</option>
                          {brands.map(b => <option key={b.slug} value={b.slug}>{b.name}</option>)}
                        </select>
                        <p className="text-[10px] text-muted-foreground mt-1">Brand name auto-leads the item title.</p>
                      </div>
                      <div><Label className="text-xs">Item title (optional)</Label>
                        <Input value={orderTitle} onChange={e => setOrderTitle(e.target.value)} className="mt-1 text-sm" placeholder="e.g. Classic Flap" data-testid="manual-order-title" />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={createManualOrder} disabled={saving} data-testid="manual-order-create-btn">Create Order</Button>
                        <Button size="sm" variant="outline" onClick={() => setOrderDialogOpen(false)}>Cancel</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 justify-start" onClick={sendMagicLink} data-testid="send-magic-link-btn">
                  <Send className="h-3.5 w-3.5" /> Send Sign-In Link
                </Button>

                <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="w-full text-xs gap-1.5 justify-start" data-testid="change-email-btn">
                      <Mail className="h-3.5 w-3.5" /> Change Email
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle className="text-sm">Change Email Address</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground">Current: <strong>{user.email}</strong></p>
                      <div><Label className="text-xs">New Email</Label>
                        <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} className="mt-1 text-sm" placeholder="new@email.com" data-testid="new-email-input" />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={changeEmail} disabled={saving} data-testid="save-email-btn">Save Email</Button>
                        <Button size="sm" variant="outline" onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-xs gap-1.5 justify-start text-red-700 border-red-200 hover:bg-red-50"
                  onClick={() => navigate(`/team/privacy?customer_id=${userId}`)}
                  data-testid="scrub-privacy-btn"
                >
                  <ShieldOff className="h-3.5 w-3.5" /> Privacy Scrub
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Grouped Accounts */}
          <Card className="bg-white border-stone/50" data-testid="grouped-accounts-card">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Link2 className="h-3 w-3" /> Grouped Accounts</h3>
              {(user.group_members || []).length === 0 ? (
                <p className="text-[10px] text-muted-foreground mb-2">Not grouped. Grouped accounts share their order history on the customer's account pages.</p>
              ) : (
                <div className="space-y-1 mb-2">
                  {user.group_members.map(m => (
                    <Link key={m.id} to={`/team/users/${m.id}`} className="block text-xs text-ink no-underline hover:underline p-1.5 rounded bg-stone/5" data-testid={`group-member-${m.id}`}>{m.email}</Link>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="text-xs gap-1 flex-1" data-testid="group-accounts-btn"><Link2 className="h-3 w-3" /> Group with...</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle className="text-sm">Group Accounts</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground">Enter the email of the account to link with <strong>{user.email}</strong>. Both accounts stay separate for sign-in, but each will see the combined order history.</p>
                      <Input value={groupEmail} onChange={e => setGroupEmail(e.target.value)} className="text-sm" placeholder="other@email.com" data-testid="group-email-input" />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={groupAccounts} disabled={saving} data-testid="group-confirm-btn">Group Accounts</Button>
                        <Button size="sm" variant="outline" onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                {(user.group_members || []).length > 0 && (
                  <Button size="sm" variant="outline" className="text-xs gap-1 text-red-700" onClick={ungroupAccount} data-testid="ungroup-btn"><Unlink className="h-3 w-3" /> Ungroup</Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Role Management */}
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Shield className="h-3 w-3" /> Role</h3>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-ink">Admin Access</span>
                  <p className="text-[10px] text-muted-foreground">Can access /team, manage orders, and configure settings</p>
                </div>
                <Switch checked={isAdmin} onCheckedChange={toggleAdmin} data-testid="admin-toggle" />
              </div>

              {isAdmin && (
                <>
                  <Separator className="my-3" />
                  <div>
                    <span className="text-xs font-medium text-ink">Staff Privilege</span>
                    <p className="text-[10px] text-muted-foreground mb-2">Full Admin has the full admin panel. Authenticator is restricted to the review queue.</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant={user.staff_role === 'authenticator' ? 'default' : 'outline'}
                        className="h-8 text-[11px] gap-1 justify-start"
                        onClick={() => setStaffRole('authenticator')}
                        data-testid="staff-role-authenticator-btn"
                      >
                        <Eye className="h-3 w-3" style={{ color: user.staff_role === 'authenticator' ? '#fff' : '#1F5E7A' }} />
                        Authenticator
                      </Button>
                      <Button
                        size="sm"
                        variant={user.staff_role === 'full_admin' ? 'default' : 'outline'}
                        className="h-8 text-[11px] gap-1 justify-start"
                        onClick={() => setStaffRole('full_admin')}
                        data-testid="staff-role-full-admin-btn"
                      >
                        <Shield className="h-3 w-3" style={{ color: user.staff_role === 'full_admin' ? '#fff' : '#0E6B5D' }} />
                        Full Admin
                      </Button>
                    </div>
                    {user.staff_role && (
                      <p className="text-[10px] text-muted-foreground mt-2" data-testid="staff-role-current">
                        Current: <strong className="text-ink">{user.staff_role === 'full_admin' ? 'Full Admin' : 'Authenticator'}</strong>
                      </p>
                    )}
                  </div>
                </>
              )}

              <Separator className="my-3" />
              <div className="text-xs">
                <span className="text-muted-foreground">Current roles: </span>
                {user.roles?.map((r, i) => <Badge key={i} variant="outline" className="text-[9px] ml-1">{r}</Badge>)}
              </div>
            </CardContent>
          </Card>

          {/* Account Status */}
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Account Status</h3>
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-ink">Active</span>
                  <p className="text-[10px] text-muted-foreground">Deactivated users cannot sign in</p>
                </div>
                <Switch checked={user.is_active !== false} onCheckedChange={toggleActive} data-testid="active-toggle" />
              </div>
            </CardContent>
          </Card>

          {/* Recent Sign-In Activity */}
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Clock className="h-3 w-3" /> Recent Activity</h3>
              {(user.recent_magic_links || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">No recent sign-in activity.</p>
              ) : (
                <div className="space-y-1">
                  {user.recent_magic_links.map((ml, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Magic link {ml.consumed ? 'used' : 'sent'}</span>
                      <span className="text-[10px] text-muted-foreground">{ml.created_at ? new Date(ml.created_at).toLocaleString() : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
